import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
// Import the SparklesIcon instead of the check or CPU icon
import {
  SparklesIcon,
  ArrowUpTrayIcon,
  ChevronRightIcon,
  DocumentIcon,
} from "@heroicons/react/24/outline";
import CustomButton from "../../components/CustomButton";
import { uploadFile } from "../../utils/helperMethods";
import { v4 as uuidv4 } from "uuid";
import {
  DocumentServicePoc,
  DocumentServicePocGet,
  generateQuestionnaire,
} from "../../api/api-methods";
import { useMutation } from "@tanstack/react-query";
import { BlockBlobClient } from "@azure/storage-blob";
import { getBlobSasToken } from "../../api/api-methods";

const RFIUploadPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [files1, setFiles1] = useState("");
  const [files2, setFiles2] = useState("");
  const [fileName1, setFileName1] = useState("");
  const [fileName2, setFileName2] = useState("");
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("");
  const fileInputRef1 = useRef();
  const fileInputRef2 = useRef();
  const newGuid = uuidv4();

  // Set up mutation
  const generateQuestionnaireMutation = useMutation({
    mutationFn: (data) => generateQuestionnaire(data),
    onSuccess: (data) => {
      setStatus("success");
      setMessage("Questionnaire generated successfully");
      setShow(true);
      setIsLoading(false);
      // Navigate to chat bot page with the questionnaire ID
      navigate(`/poc-chatBot?id=${data.filledQuestionnaire_id}`);
    },
    onError: (error) => {
      setStatus("error");
      setMessage("Failed to generate questionnaire. Please try again.");
      setShow(true);
      setIsLoading(false);
      console.error("Error generating questionnaire:", error);
    },
  });
  const [file1, setFile1] = useState();
  const [file2, setFile2] = useState();

  const handleButtonClick1 = () => {
    fileInputRef1.current.click();
  };

  const handleButtonClick2 = () => {
    fileInputRef2.current.click();
  };

  const handleFileChange1 = async (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFileName1(selectedFile.name);
      setFiles1(selectedFile);
      try {
        const blobUrl = await uploadFile(selectedFile, newGuid, "vqa-poc");
      } catch (error) {
        setStatus("error");
        setMessage("Error uploading file 1");
        setShow(true);
      }
    }
  };

  const handleFileChange2 = async (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFileName2(selectedFile.name);
      setFiles2(selectedFile);
      try {
        const blobUrl = await uploadFile(selectedFile, newGuid, "vqa-poc");
      } catch (error) {
        console.error("Error uploading file 2:", error);
        setStatus("error");
        setMessage("Error uploading file 2");
        setShow(true);
      }
    }
  };

  const handleNext = async () => {
    if (!fileName1 || !fileName2) {
      setStatus("error");
      setMessage("Please upload both RFI files");
      setShow(true);
      return;
    }

    setIsLoading(true);
    try {
      // Send the file names to the API
      generateQuestionnaireMutation.mutate({
        rfiFile1Name: fileName1,
        rfiFile2Name: fileName2,
      });
    } catch (error) {
      setStatus("error");
      setMessage("Error processing files. Please try again.");
      setShow(true);
      console.error("Error processing files:", error);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-[100%] flex flex-col bg-gradient-to-b from-slate-50 to-white">
      {/* Custom style for date inputs */}
      <style jsx>{`
        input[type="date"]::-webkit-calendar-picker-indicator {
          filter: grayscale(100%);
          opacity: 0.5;
        }
        input[type="date"] {
          color: #6b7280; /* text-gray-500 */
        }
      `}</style>
      {/* Header Section */}
      <div className="p-7 border-y border-gray-300 bg-slate-50 mb-10">
        <div className="flex items-center text-sm">
          <h1 className="text-gray-500 cursor-pointer font-semibold">POC</h1>
          <ChevronRightIcon className="w-5 h-5 text-gray-400" />
        </div>

        <div className="mt-5 flex items-center">
          <span className="bg-white border border-gray-300 rounded-md px-7 py-5 mr-5">
            <SparklesIcon className="h-9 w-9 text-blue-500" />
          </span>
          <span>
            <p className="font-semibold text-md">Generate VQA</p>
            <p className="text-sm text-gray-500">
              Upload RFI files to generate questionnaire
            </p>
          </span>
        </div>
      </div>

      {/* Main Content - Full Screen */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-4xl bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                Upload Documents
              </h2>
              <span className="text-sm text-gray-500">
                PDF or Word files only
              </span>
            </div>

            {/* Upload Boxes Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* First Upload Box */}
              <div className="relative">
                <input
                  type="file"
                  id="file-upload-1"
                  className="hidden"
                  onChange={handleFileChange1}
                  ref={fileInputRef1}
                  accept=".pdf,.doc,.docx"
                />
                <div
                  onClick={handleButtonClick1}
                  className={`
                    cursor-pointer group h-full
                    border-2 border-dashed rounded-xl p-8
                    flex items-center justify-center
                    ${
                      fileName1
                        ? "border-indigo-200 bg-indigo-50"
                        : "border-gray-200 hover:border-indigo-300"
                    }
                    transition-all duration-200 hover:shadow-md
                  `}
                >
                  <div className="flex flex-col items-center">
                    {fileName1 ? (
                      <>
                        <DocumentIcon className="h-12 w-12 text-indigo-600 mb-3" />
                        <p className="text-sm font-medium text-indigo-700 text-center">
                          {fileName1}
                        </p>
                      </>
                    ) : (
                      <>
                        <ArrowUpTrayIcon className="h-12 w-12 text-gray-400 group-hover:text-indigo-600 transition-colors duration-200" />
                        <p className="mt-4 text-base font-medium text-center text-gray-500 group-hover:text-indigo-600 transition-colors duration-200">
                          Click to upload first RFI file
                        </p>
                        <p className="mt-2 text-sm text-gray-400">
                          PDF or Word files
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Second Upload Box */}
              <div className="relative">
                <input
                  type="file"
                  id="file-upload-2"
                  className="hidden"
                  onChange={handleFileChange2}
                  ref={fileInputRef2}
                  accept=".pdf,.doc,.docx"
                />
                <div
                  onClick={handleButtonClick2}
                  className={`
                    cursor-pointer group h-full
                    border-2 border-dashed rounded-xl p-8
                    flex items-center justify-center
                    ${
                      fileName2
                        ? "border-indigo-200 bg-indigo-50"
                        : "border-gray-200 hover:border-indigo-300"
                    }
                    transition-all duration-200 hover:shadow-md
                  `}
                >
                  <div className="flex flex-col items-center">
                    {fileName2 ? (
                      <>
                        <DocumentIcon className="h-12 w-12 text-indigo-600 mb-3" />
                        <p className="text-sm font-medium text-indigo-700 text-center">
                          {fileName2}
                        </p>
                      </>
                    ) : (
                      <>
                        <ArrowUpTrayIcon className="h-12 w-12 text-gray-400 group-hover:text-indigo-600 transition-colors duration-200" />
                        <p className="mt-4 text-base font-medium text-center text-gray-500 group-hover:text-indigo-600 transition-colors duration-200">
                          Click to upload second RFI file
                        </p>
                        <p className="mt-2 text-sm text-gray-400">
                          PDF or Word files
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Footer */}
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-center sm:justify-end">
            <button
              onClick={handleNext}
              disabled={isLoading || !fileName1 || !fileName2}
              className={`
                flex items-center space-x-2 px-6 py-3 rounded-lg
                w-full sm:w-auto
                ${
                  isLoading || !fileName1 || !fileName2
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-indigo-600 text-white hover:bg-indigo-700"
                }
                transition-all duration-200 shadow-sm hover:shadow
              `}
            >
              <span>{isLoading ? "Processing..." : "Analyze Documents"}</span>
              {!isLoading && <ChevronRightIcon className="h-5 w-5 ml-2" />}
            </button>
          </div>
        </div>
      </div>

      {/* AI Processing Loader */}
      {isLoading && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full mx-4">
            <div className="flex flex-col items-center">
              {/* Pulse Animation Container */}
              <div className="relative mb-6">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center">
                  <SparklesIcon className="h-8 w-8 text-indigo-600" />
                  {/* Ripple Effects */}
                  <div className="absolute inset-0 rounded-full animate-ping bg-indigo-400 opacity-20"></div>
                  <div
                    className="absolute inset-0 rounded-full animate-pulse bg-indigo-400 opacity-10"
                    style={{ animationDelay: "300ms" }}
                  ></div>
                </div>
                {/* Moving Dots */}
                <div className="flex justify-center mt-4 space-x-1">
                  <div
                    className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce"
                    style={{ animationDelay: "0ms" }}
                  ></div>
                  <div
                    className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce"
                    style={{ animationDelay: "150ms" }}
                  ></div>
                  <div
                    className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce"
                    style={{ animationDelay: "300ms" }}
                  ></div>
                </div>
              </div>

              {/* Processing Text */}
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  AI Processing
                </h3>
                <p className="text-sm text-gray-500">
                  Analyzing your documents with advanced AI...
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {show && (
        <div
          className={`fixed top-4 right-4 p-4 rounded-md ${
            status === "success"
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {message}
        </div>
      )}
    </div>
  );
};

export default RFIUploadPage;
